import { useState, useRef, useCallback, ReactNode, useEffect } from 'react';

interface CollapsiblePanelProps {
  title: string;
  children: ReactNode;
  autoHideInFullscreen?: boolean;
}

export function CollapsiblePanel({ title, children, autoHideInFullscreen = true }: CollapsiblePanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hideTimeoutRef = useRef<number>();

  // Clamp any position so the panel always stays fully inside the viewport.
  const clamp = useCallback((x: number, y: number) => {
    const w = panelRef.current?.offsetWidth ?? 300;
    const h = panelRef.current?.offsetHeight ?? 200;
    const margin = 8;
    const maxX = Math.max(margin, window.innerWidth - w - margin);
    const maxY = Math.max(margin, window.innerHeight - h - margin);
    return {
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    };
  }, []);

  // Initial placement: horizontally centered, near the top (just below the header).
  useEffect(() => {
    if (position === null && panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setPosition(clamp((window.innerWidth - rect.width) / 2, 76));
    }
  }, [position, clamp]);

  // Keep the panel on-screen whenever the window is resized (fixes it
  // "disappearing" when the browser window is made smaller).
  useEffect(() => {
    const onResize = () => setPosition((prev) => (prev ? clamp(prev.x, prev.y) : prev));
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [clamp]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, input, select')) return;
    setIsDragging(true);
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
  }, []);

  // Pointer events cover mouse, touch and pen with a single code path.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: PointerEvent) => {
      setPosition(clamp(e.clientX - dragOffset.current.x, e.clientY - dragOffset.current.y));
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [isDragging, clamp]);

  // Auto-hide in fullscreen after 4s of inactivity (revealed by mouse move or touch).
  useEffect(() => {
    if (!autoHideInFullscreen) return;

    const resetTimer = () => {
      setIsHidden(false);
      clearTimeout(hideTimeoutRef.current);
      if (document.fullscreenElement) {
        hideTimeoutRef.current = window.setTimeout(() => setIsHidden(true), 4000);
      }
    };

    const onFullscreenChange = () => {
      if (document.fullscreenElement) resetTimer();
      else { setIsHidden(false); clearTimeout(hideTimeoutRef.current); }
    };

    document.addEventListener('mousemove', resetTimer);
    document.addEventListener('touchstart', resetTimer);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    if (document.fullscreenElement) resetTimer();

    return () => {
      document.removeEventListener('mousemove', resetTimer);
      document.removeEventListener('touchstart', resetTimer);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      clearTimeout(hideTimeoutRef.current);
    };
  }, [autoHideInFullscreen]);

  const positionStyle = position
    ? { left: position.x, top: position.y }
    : { left: '50%', top: '76px', transform: 'translateX(-50%)' };

  return (
    <div
      ref={panelRef}
      className={`fixed z-50 select-none transition-[opacity,transform] duration-300 ${isHidden ? 'opacity-0 pointer-events-none -translate-y-2' : 'opacity-100 translate-y-0'}`}
      style={{
        ...positionStyle,
        maxWidth: 'calc(100vw - 16px)',
        background: 'rgba(10, 10, 18, 0.88)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: '16px',
        boxShadow: '0 16px 60px rgba(0,0,0,0.5)',
      }}
    >
      {/* Header / drag handle */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-move transition-colors duration-150 rounded-t-2xl touch-none ${isDragging ? 'bg-white/5' : ''}`}
        style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', touchAction: 'none' }}
        onPointerDown={handlePointerDown}
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
            <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
            <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
          </div>
          <span className="text-sm font-semibold text-white/80">{title}</span>
        </div>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-3.5 w-3.5 text-white/40 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className={`overflow-hidden transition-all duration-250 ${isCollapsed ? 'max-h-0' : 'max-h-[70vh] overflow-y-auto'}`}>
        <div className="p-4 w-[min(320px,86vw)]">{children}</div>
      </div>
    </div>
  );
}
